<template>
    <section class="pu-card">
        <div class="pu-header" v-if="isPostRaciLoaded">
            <i class="fal fa-users pu-header-icon"></i>
            <h2 class="pu-header-title">{{ $t('post.page.racis.title') }}</h2>
            <button class="pu-edit-btn" @click="openRacisForm" :title="$t('post.page.racis.edit')">
                <i class="fal fa-pencil"></i>
            </button>
        </div>
        <div class="pu-body" v-if="isPostRaciLoaded">
            <ul role="list" v-if="post.racis" class="pu-list">
                <form-racis
                    v-for="(racis, index) in post.racis"
                    :key="index"
                    :postracis="racis"
                />
            </ul>
        </div>

        <BmvModal v-model="modalOpen" size="lg" :closable="true" @close="closeRacisForm">
            <template #header>
                <div class="pu-modal-head">
                    <span class="pu-modal-head-icon">
                        <i class="fal fa-users"></i>
                    </span>
                    <div class="pu-modal-head-body">
                        <h2 class="pu-modal-head-title">
                            {{ $t('post.page.racis.edit') }}
                        </h2>
                        <span class="pu-modal-head-sub">
                            {{ $t('post.page.racis.description') }}
                        </span>
                    </div>
                </div>
            </template>

            <FormRacisForm
                ref="formRacisForm"
                :formpopup="true"
                @get-post-data="getPostData"
                @saved="onSaved"
            />

            <template #footer>
                <BmvButton variant="ghost" size="md" @click="closeRacisForm">
                    {{ $t('post.page.racis.cancel') }}
                </BmvButton>
                <BmvButton
                    variant="primary"
                    size="md"
                    :loading="saving"
                    icon="fal fa-save"
                    @click="updateRacisForm"
                >
                    {{ $t('post.page.racis.register') }}
                </BmvButton>
            </template>
        </BmvModal>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import FormRacisForm from '@/components/Model/Form/FormComponents/FormRacisForm.vue'
import FormRacis from '@/components/Model/Form/FormRacis.vue'
import BmvModal from '@/components/UI/v2/BmvModal.vue'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import { mapState, mapActions } from 'vuex'

export default {
    name: 'PostUsers',
    components: {
        FormRacisForm,
        FormRacis,
        BmvModal,
        BmvButton
    },
    data() {
        return {
            modalOpen: false,
            saving: false
        }
    },
    computed: {
        ...mapState('post', ['post']),
        isPostRaciLoaded: {
            get() {
                return this.$store.state.post.isPostRaciLoaded
            },
            set(value) {
                this.$store.commit('post/setIsPostRaciLoaded', value)
            }
        }
    },
    methods: {
        ...mapActions('post', ['getPostHistories']),
        getPostData() {
            this.$emit('get-post-data')
        },
        openRacisForm() {
            this.modalOpen = true
            this.isPostRaciLoaded = false
        },
        closeRacisForm() {
            this.modalOpen = false
            this.isPostRaciLoaded = true
            this.saving = false
        },
        updateRacisForm() {
            if (this.saving) return
            this.saving = true
            this.$refs.formRacisForm
                .update()
                .then(() => {
                    this.getPostHistories(this.post.id)
                })
                .catch((e) => {
                    this.saving = false
                    this.$store.dispatch('dispatchError', e)
                })
        },
        onSaved() {
            this.closeRacisForm()
        }
    }
}
</script>

<style scoped>
.pu-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.pu-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
}

.pu-header-icon {
    font-size: 1rem;
    color: #0891B2;
    flex-shrink: 0;
}

.pu-header-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    line-height: 1.2;
}

.pu-edit-btn {
    margin-left: auto;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.78rem;
    transition: all 0.2s;
}

.pu-edit-btn:hover {
    background: rgba(8, 145, 178, 0.08);
    color: #0891B2;
    border-color: rgba(8, 145, 178, 0.3);
}

.pu-body {
    padding: 0;
}

.pu-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

/* Modal header */
.pu-modal-head {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
}

.pu-modal-head-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(8, 145, 178, 0.12);
    color: #0891B2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    flex-shrink: 0;
}

.pu-modal-head-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.pu-modal-head-title {
    margin: 0;
    font-size: var(--v2-text-lg-size);
    line-height: var(--v2-text-lg-line);
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-semibold);
}

.pu-modal-head-sub {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.4;
}
</style>
